import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, UserIcon, MessageSquareIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
interface TimelineEvent {
  id: string;
  timestamp: string;
  type: 'created' | 'updated' | 'approved' | 'rejected' | 'comment';
  user: string;
  description: string;
  comment?: string;
}
interface Comment {
  id: string;
  user: string;
  role: string;
  timestamp: string;
  message: string;
}
interface RequestDetail {
  id: string;
  type: string;
  description: string;
  requestedBy: string;
  requestedAt: string;
  status: string;
  details: {
    paymentMethod?: string;
    roleName?: string;
    reason?: string;
    permissions?: string;
    location?: string;
    brand?: string;
    model?: string;
    terminalName?: string;
    rejectionReason?: string;
  };
  timeline: TimelineEvent[];
  comments: Comment[];
}
export function RequestDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [newComment, setNewComment] = useState('');
  // Mock data - in real app, fetch based on id
  const [request, setRequest] = useState<RequestDetail>(() => {
    const now = new Date();
    if (location.state && typeof location.state === 'object' && 'location' in location.state && 'brand' in location.state && 'model' in location.state && 'terminalName' in location.state) {
      const terminalData = location.state as {
        location: string;
        brand: string;
        model: string;
        terminalName: string;
      };
      return {
        id: id || 'REQ-001',
        type: 'Terminal Request',
        description: `Request for Terminal ${terminalData.terminalName} Activation`,
        requestedBy: 'Simon Lee',
        requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'Pending',
        details: {
          location: terminalData.location,
          brand: terminalData.brand,
          model: terminalData.model,
          terminalName: terminalData.terminalName
        },
        timeline: [{
          id: 'evt-1',
          timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'created',
          user: 'Simon Lee',
          description: 'Request created'
        }, {
          id: 'evt-2',
          timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'comment',
          user: 'Admin User',
          description: 'Comment added',
          comment: 'We are reviewing your terminal request. Additional documentation may be required.'
        }, {
          id: 'evt-3',
          timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'updated',
          user: 'System',
          description: 'Request assigned to terminal team'
        }],
        comments: [{
          id: 'cmt-1',
          user: 'Admin User',
          role: 'Administrator',
          timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
          message: 'We are reviewing your terminal request. Additional documentation may be required.'
        }]
      };
    }
    return {
      id: id || 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      },
      timeline: [{
        id: 'evt-1',
        timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'created',
        user: 'Simon Lee',
        description: 'Request created'
      }, {
        id: 'evt-2',
        timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'comment',
        user: 'Admin User',
        description: 'Comment added',
        comment: 'We are reviewing your request. Additional documentation may be required.'
      }, {
        id: 'evt-3',
        timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'updated',
        user: 'System',
        description: 'Request assigned to compliance team'
      }],
      comments: [{
        id: 'cmt-1',
        user: 'Admin User',
        role: 'Administrator',
        timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
        message: 'We are reviewing your request. Additional documentation may be required.'
      }]
    };
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      case 'Pending':
        return 'bg-amber-100 text-amber-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Closed':
        return <CheckCircleIcon size={20} className="text-gray-600" data-id="element-14721" />;
      case 'Pending':
        return <AlertCircleIcon size={20} className="text-amber-600" data-id="element-14722" />;
      case 'In Progress':
        return <ClockIcon size={20} className="text-blue-600" data-id="element-14723" />;
      default:
        return <ClockIcon size={20} className="text-gray-600" data-id="element-14724" />;
    }
  };
  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'created':
        return <CheckCircleIcon size={16} className="text-blue-600" data-id="element-14725" />;
      case 'approved':
        return <CheckCircleIcon size={16} className="text-green-600" data-id="element-14726" />;
      case 'rejected':
        return <XCircleIcon size={16} className="text-red-600" data-id="element-14727" />;
      case 'comment':
        return <MessageSquareIcon size={16} className="text-purple-600" data-id="element-14728" />;
      default:
        return <ClockIcon size={16} className="text-gray-600" data-id="element-14729" />;
    }
  };
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const now = new Date();
    const comment: Comment = {
      id: `cmt-${Date.now()}`,
      user: 'Simon Lee',
      role: 'Owner',
      timestamp: now.toISOString(),
      message: newComment
    };
    const timelineEvent: TimelineEvent = {
      id: `evt-${Date.now()}`,
      timestamp: now.toISOString(),
      type: 'comment',
      user: 'Simon Lee',
      description: 'Comment added',
      comment: newComment
    };
    setRequest({
      ...request,
      comments: [...request.comments, comment],
      timeline: [...request.timeline, timelineEvent].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    });
    setNewComment('');
  };
  return <div className="max-w-7xl mx-auto py-8 px-4" data-id="element-14730">
      {/* Header */}
      <div className="mb-6" data-id="element-14731">
        <button onClick={() => navigate('/requests')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4" data-id="element-14732">
          <ArrowLeftIcon size={20} data-id="element-14733" />
          <span data-id="element-14734">Back to Requests</span>
        </button>
        <div className="flex items-start justify-between" data-id="element-14735">
          <div data-id="element-14736">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-14737">
              {request.id}
            </h1>
            <p className="text-gray-600" data-id="element-14738">{request.description}</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-14739">
            {getStatusIcon(request.status)}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`} data-id="element-14740">
              {request.status}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-14741">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-14742">
          {/* Request Details */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-14743">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-14744">
              <h3 className="text-md font-semibold text-gray-900" data-id="element-14745">
                Request Details
              </h3>
            </div>
            <div className="p-6 space-y-4" data-id="element-14746">
              <div className="grid grid-cols-2 gap-4" data-id="element-14747">
                <div data-id="element-14748">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14749">
                    Type
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14750">
                    {request.type}
                  </p>
                </div>
                <div data-id="element-14751">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14752">
                    Requested By
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14753">
                    {request.requestedBy}
                  </p>
                </div>
                <div data-id="element-14754">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14755">
                    Requested At
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14756">
                    {formatTimestamp(request.requestedAt)}
                  </p>
                </div>
                <div data-id="element-14757">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14758">
                    Status
                  </p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-14759">
                    {request.status}
                  </span>
                </div>
              </div>

              {request.details.paymentMethod && <div data-id="element-14760">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14761">
                    Payment Method
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14762">
                    {request.details.paymentMethod}
                  </p>
                </div>}

              {request.details.roleName && <div data-id="element-14763">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14764">
                    Role Name
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14765">
                    {request.details.roleName}
                  </p>
                </div>}

              {request.details.reason && <div data-id="element-14766">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14767">
                    Reason
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-14768">
                    {request.details.reason}
                  </p>
                </div>}

              {request.details.permissions && <div data-id="element-14769">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14770">
                    Permissions
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-14771">
                    {request.details.permissions}
                  </p>
                </div>}

              {request.details.location && <div data-id="element-14772">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14773">
                    Location
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14774">
                    {request.details.location}
                  </p>
                </div>}

              {request.details.brand && <div data-id="element-14775">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14776">
                    Brand
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14777">
                    {request.details.brand}
                  </p>
                </div>}

              {request.details.model && <div data-id="element-14778">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14779">
                    Model
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14780">
                    {request.details.model}
                  </p>
                </div>}

              {request.details.terminalName && <div data-id="element-14781">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14782">
                    Terminal Name
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14783">
                    {request.details.terminalName}
                  </p>
                </div>}

              {request.details.rejectionReason && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-14784">
                  <p className="text-xs text-red-500 uppercase tracking-wider mb-1" data-id="element-14785">
                    Rejection Reason
                  </p>
                  <p className="text-sm text-red-700" data-id="element-14786">
                    {request.details.rejectionReason}
                  </p>
                </div>}
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-14787">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-14788">
              <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-14789">
                <MessageSquareIcon size={18} className="text-gray-600" data-id="element-14790" />
                Comments ({request.comments.length})
              </h3>
            </div>
            <div className="p-6 space-y-4" data-id="element-14791">
              {request.comments.length === 0 ? <p className="text-sm text-gray-500 text-center py-4" data-id="element-14792">
                  No comments yet. Be the first to comment!
                </p> : <div className="space-y-4" data-id="element-14793">
                  {request.comments.map(comment => <div key={comment.id} className="border border-gray-200 rounded-lg p-4" data-id="element-14794">
                      <div className="flex items-start gap-3" data-id="element-14795">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold flex-shrink-0" data-id="element-14796">
                          {comment.user.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="flex-1" data-id="element-14797">
                          <div className="flex items-center justify-between mb-1" data-id="element-14798">
                            <div data-id="element-14799">
                              <p className="text-sm font-medium text-gray-900" data-id="element-14800">
                                {comment.user}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-14801">
                                {comment.role}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500" data-id="element-14802">
                              {formatTimestamp(comment.timestamp)}
                            </p>
                          </div>
                          <p className="text-sm text-gray-700" data-id="element-14803">
                            {comment.message}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div>}

              {/* Add Comment Form */}
              <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-14804">
                <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" placeholder="Add a comment..." rows={3} value={newComment} onChange={e => setNewComment(e.target.value)} data-id="element-14805" />
                <div className="flex justify-end mt-2" data-id="element-14806">
                  <button onClick={handleAddComment} disabled={!newComment.trim()} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#C41B14] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-14807">
                    Add Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Sidebar */}
        <div className="lg:col-span-1" data-id="element-14808">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden sticky top-4" data-id="element-14809">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-14810">
              <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-14811">
                <ClockIcon size={18} className="text-gray-600" data-id="element-14812" />
                Timeline
              </h3>
            </div>
            <div className="p-6" data-id="element-14813">
              <div className="space-y-4" data-id="element-14814">
                {request.timeline.map((event, index) => <div key={event.id} className="flex gap-3" data-id="element-14815">
                    <div className="flex flex-col items-center" data-id="element-14816">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-14817">
                        {getTimelineIcon(event.type)}
                      </div>
                      {index < request.timeline.length - 1 && <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-14818" />}
                    </div>
                    <div className="flex-1 pb-4" data-id="element-14819">
                      <p className="text-sm font-medium text-gray-900" data-id="element-14820">
                        {event.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-14821">
                        {event.user} • {formatTimestamp(event.timestamp)}
                      </p>
                      {event.comment && <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded" data-id="element-14822">
                          {event.comment}
                        </p>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}